import type { ReactNode } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { Eyebrow } from './kit';

/**
 * One feature of the product: the claim and its proof points beside a
 * product mock-up on a stage. `reverse` puts the mock-up first on desktop.
 */
export default function Feature({
  id,
  eyebrow,
  title,
  tail,
  lead,
  points,
  visual,
  description,
  reverse = false,
  stage = 'lavender',
  halftoneAt,
}: {
  id?: string;
  eyebrow: string;
  title: string;
  tail?: string;
  lead: string;
  points: string[];
  visual: ReactNode;
  /** What the mock-up shows, for screen readers (the mock-up is hidden). */
  description: string;
  reverse?: boolean;
  stage?: 'indigo' | 'lavender' | 'ink';
  halftoneAt?: string;
}) {
  const { ref, state } = useReveal<HTMLElement>(0.2);

  return (
    <section
      id={id}
      ref={ref}
      data-reveal={state}
      className={clsxm(styles.reveal, 'scroll-mt-8 py-16 sm:py-24')}
    >
      <div className='mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div
          className={clsxm(
            styles.rv,
            'lg:col-span-5',
            reverse && 'lg:order-last',
          )}
        >
          <Eyebrow>{eyebrow}</Eyebrow>
          <SectionHeading size='md' title={title} tail={tail} lead={lead} />
          <ul className='mt-8 space-y-3.5'>
            {points.map((point) => (
              <li
                key={point}
                className={clsxm('flex gap-3 text-[15px] leading-6', tone.body)}
              >
                <span className='mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-50 text-primary-700 dark:bg-primary-400/15 dark:text-primary-300'>
                  <CheckGlyph className='h-3 w-3' />
                </span>
                {point}
              </li>
            ))}
          </ul>
        </div>

        <div
          className={clsxm(styles.rv, 'min-w-0 lg:col-span-7')}
          style={delay(120)}
        >
          <Stage
            variant={stage}
            halftoneAt={halftoneAt ?? (reverse ? '0% 0%' : '100% 0%')}
            className='px-3 py-6 sm:px-8 sm:py-10'
          >
            <p className='sr-only'>{description}</p>
            <div aria-hidden='true'>{visual}</div>
          </Stage>
        </div>
      </div>
    </section>
  );
}
